import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  DollarSign, 
  Users, 
  Clock, 
  FileText,
  Loader2,
  Lock,
  Download,
  Calendar
} from 'lucide-react';

const Reports = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [period, setPeriod] = useState('month');

  const isPaidUser = user?.subscription_plan && user.subscription_plan !== 'free';

  useEffect(() => {
    if (isPaidUser) {
      fetchReportData();
    } else {
      setLoading(false);
    }
  }, [period, isPaidUser]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/reports?period=${period}`);
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    try {
      const response = await api.get(`/reports/download?period=${period}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `createcollect_report_${period}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  // Show upgrade prompt for free users
  if (!isPaidUser) {
    return (
      <Layout>
        <div className="p-6 lg:p-12 max-w-4xl mx-auto">
          <Card className="text-center py-16">
            <CardContent>
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Reports & Analytics</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Get detailed insights into your business with revenue summaries, top clients, 
                payment trends, and downloadable reports.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mb-8">
                <div className="p-4 rounded-lg bg-muted/50">
                  <TrendingUp className="w-6 h-6 mx-auto mb-2 text-chart-5" />
                  <p className="text-sm font-medium">Revenue Trends</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <Users className="w-6 h-6 mx-auto mb-2 text-chart-2" />
                  <p className="text-sm font-medium">Top Clients</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <Clock className="w-6 h-6 mx-auto mb-2 text-chart-3" />
                  <p className="text-sm font-medium">Payment Speed</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <Download className="w-6 h-6 mx-auto mb-2 text-chart-4" />
                  <p className="text-sm font-medium">Export Reports</p>
                </div>
              </div>
              <Button onClick={() => navigate('/settings')} size="lg" className="gap-2">
                Upgrade to Unlock Reports
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-12 space-y-8" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl tracking-tight flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-primary" />
              Reports
            </h1>
            <p className="text-muted-foreground mt-1">
              Business insights and analytics
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40" data-testid="period-select">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="quarter">This Quarter</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={downloadReport} className="gap-2" data-testid="download-report-btn">
              <Download className="w-4 h-4" />
              Export
            </Button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Revenue</p>
                  <p className="text-2xl font-bold mt-1">
                    {formatCurrency(reportData?.total_revenue || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-chart-5/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-chart-5" />
                </div>
              </div>
              {reportData?.revenue_change !== undefined && (
                <div className={`flex items-center gap-1 mt-2 text-sm ${reportData.revenue_change >= 0 ? 'text-chart-5' : 'text-chart-4'}`}>
                  {reportData.revenue_change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  <span>{Math.abs(reportData.revenue_change)}% vs previous period</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Outstanding</p>
                  <p className="text-2xl font-bold mt-1 text-chart-3">
                    {formatCurrency(reportData?.total_outstanding || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-chart-3/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-chart-3" />
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Awaiting payment
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Paid This Month</p>
                  <p className="text-2xl font-bold mt-1 text-chart-5">
                    {formatCurrency(reportData?.total_paid_this_month || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-chart-5/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-chart-5" />
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Collected this month
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Second row stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Invoices Sent</p>
                  <p className="text-2xl font-bold mt-1">{reportData?.invoices_sent || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-chart-2/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-chart-2" />
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {reportData?.invoices_paid || 0} paid, {reportData?.invoices_pending || 0} pending
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg. Payment Time</p>
                  <p className="text-2xl font-bold mt-1">{reportData?.avg_payment_days || 0} days</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-chart-4/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-chart-4" />
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Time from sent to paid
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Clients</p>
                  <p className="text-2xl font-bold mt-1">{reportData?.active_clients || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-chart-1/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-chart-1" />
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Clients invoiced this period
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Clients */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Top Clients
              </CardTitle>
              <CardDescription>By total revenue</CardDescription>
            </CardHeader>
            <CardContent>
              {reportData?.top_clients?.length > 0 ? (
                <div className="space-y-4">
                  {reportData.top_clients.map((client, index) => (
                    <div key={client.client_id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          index === 0 ? 'bg-chart-3/15 text-chart-3' :
                          index === 1 ? 'bg-muted text-muted-foreground' :
                          index === 2 ? 'bg-chart-1/15 text-chart-1' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium">{client.name}</p>
                          <p className="text-sm text-muted-foreground">{client.invoice_count} invoices</p>
                        </div>
                      </div>
                      <p className="font-mono font-medium">{formatCurrency(client.total_revenue)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No client data for this period</p>
              )}
            </CardContent>
          </Card>

          {/* Monthly Revenue */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Revenue Breakdown
              </CardTitle>
              <CardDescription>By status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-chart-5/10">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-chart-5"></div>
                    <span>Paid</span>
                  </div>
                  <span className="font-mono font-medium">{formatCurrency(reportData?.revenue_by_status?.paid || 0)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-chart-2/10">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-chart-2"></div>
                    <span>Partially Paid</span>
                  </div>
                  <span className="font-mono font-medium">{formatCurrency(reportData?.revenue_by_status?.partially_paid || 0)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-chart-3/10">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-chart-3"></div>
                    <span>Pending</span>
                  </div>
                  <span className="font-mono font-medium">{formatCurrency(reportData?.revenue_by_status?.sent || 0)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-chart-4/10">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-chart-4"></div>
                    <span>Overdue</span>
                  </div>
                  <span className="font-mono font-medium">{formatCurrency(reportData?.revenue_by_status?.overdue || 0)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Payment Activity</CardTitle>
            <CardDescription>Recent payments received</CardDescription>
          </CardHeader>
          <CardContent>
            {reportData?.recent_payments?.length > 0 ? (
              <div className="space-y-3">
                {reportData.recent_payments.map((payment, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div>
                      <p className="font-medium">{payment.client_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {payment.invoice_number} • {new Date(payment.paid_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-medium text-chart-5">+{formatCurrency(payment.amount)}</p>
                      <Badge variant="outline" className="text-xs">{payment.payment_method || 'Manual'}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">No payments in this period</p>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Reports;
